import React, {useEffect, useCallback} from 'react';
import {isNumber} from "utils/IsNumber.js"
import {
    unmapPlanarParameters} from 'operations/actions.js';
import {
    setSelectedZObject} from 'actions/index.js';
import {WIDTH, HEIGHT, PADDING} from './constants.js';

const UP = 'UP';
const DOWN = 'DOWNW';
const RIGHT = 'RIGHT';
const LEFT = 'LEFT';

export const useKey = ({
    selectedDensity,
    setDensities,
    effect,
    isRegionMode,
    filteredParameters,
    setSelected,
    selected,
    setFilteredParameters,
    sequencerEngine,
    setEffectParameterValues,
    setParameterMinMax
}) => {
    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener(
            "keydown", onKeyDown);
    }, [setSelected, isRegionMode, selected, filteredParameters, effect, selectedDensity, setDensities]);

    const onKeyDown = useCallback((e) => {
        if (e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA') {
            return;
        }

        if (effect.id !== effect.track.sequencerEngine.state.effects.selectedEffect) {
            return;
        }

        if (effect !== effect.track.sequencerEngine.state.zobject.selectedZObject) {
            return;
        }

        if (isNumber("" + e.key)) {
            handleNumber(parseInt(e.key));
            return;
        }

        if (e.key === 'a' && (
            e.metaKey || e.ctrlKey)) {
            selectAll();
            sequencerEngine.dispatch(setSelectedZObject(effect));
        } else if (e.key === 'Backspace') {
            if (selectedDensity) {
                deleteSelectedDensity();
            } else {
                unmap();
            }
            sequencerEngine.dispatch(setSelectedZObject(effect));
        } else if (e.key === 's' && (
            e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            effect.takeSnapshot(true);
            sequencerEngine.dispatch(setSelectedZObject(effect));
        } else if (e.key === 'g' && (
            e.metaKey || e.ctrlKey)) {
            group();
            sequencerEngine.dispatch(setSelectedZObject(effect));
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            arrow(LEFT);
            sequencerEngine.dispatch(setSelectedZObject(effect));
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            arrow(RIGHT);
            sequencerEngine.dispatch(setSelectedZObject(effect));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            arrow(UP);
            sequencerEngine.dispatch(setSelectedZObject(effect));
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            arrow(DOWN);
            sequencerEngine.dispatch(setSelectedZObject(effect));
        }
    }, [setSelected, isRegionMode, selected, filteredParameters, effect, selectedDensity]);

    const arrow = useCallback((direction) => {
        if (effect.groups.some(x => x.variations && x.variations.length > 0) ||
            effect.variations && effect.variations.length > 0) {
            // if we're in genetic mode then dont allow arrow keys
            return;
        }
        let xDiff = direction === LEFT ? -1 : direction === RIGHT ? 1 : 0;
        let yDiff = direction === UP ? -1 : direction === DOWN ? 1 : 0;
        if (isRegionMode) {
            xDiff *= 4;
            yDiff *= 4;
            
            // then we need to move every  min/max in the direction
            let minMax = {};
            let values = {};
            for (let param of selected) {
                let diff = (param.max - param.min)/WIDTH;
                param.min = Math.max(
                    Math.min(param.min + diff * xDiff, param.originalMax),
                    param.originalMin);
                param.max = Math.max(
                    Math.min(param.max + diff * xDiff, param.originalMax),
                    param.originalMin);
                minMax[param.id] = {min: param.min, max: param.max};

                if (param.depthParameter) {
                    let _param = param.depthParameter;
                    diff = (_param.max - _param.min)/WIDTH;
                    _param.min = Math.max(
                        Math.min(_param.min + diff * xDiff, _param.originalMax),
                        _param.originalMin);
                    _param.max = Math.max(
                        Math.min(_param.max + diff * xDiff, _param.originalMax),
                        _param.originalMin);
                    minMax[_param.id] = {min: _param.min, max: _param.max};
                }
                values[param.id] = param.get(true);
            }
            setParameterMinMax(minMax);
            setEffectParameterValues(minMax);
        } else {
            let bulk = {};
            for (let param of selected) {
                if (param.subMacros) {
                    // its a macro...
                    continue;
                }
                let {id, value} = param.moveCoordinates(xDiff*2, yDiff*2);
                bulk[id] = value;
            }
            setEffectParameterValues(bulk);
        }
    }, [isRegionMode, selected])

    const handleNumber = useCallback((number) => {
        if (selectedDensity) {
            selectedDensity.viscosity = number;
            setDensities([... effect.regions]);
        }
    }, [selectedDensity, setDensities]);

    const deleteSelectedDensity = useCallback((number) => {
        if (selectedDensity) {
            effect.deleteDensity(selectedDensity);
            setDensities([... effect.regions]);
        }
    }, [selectedDensity, setDensities]);

    const selectAll = useCallback(() => {
        if (selected && selected.length > 0) {
            setSelected([]);
            return;
        }

        if (filteredParameters) {
            setSelected([... filteredParameters]);
        } else {
            setSelected([... effect._parameters]);
        }
    }, [setSelected, selected, filteredParameters, effect, setFilteredParameters]);

    const unmap = useCallback(() => {
        sequencerEngine.operationManager.dispatch(
            unmapPlanarParameters(effect, selected));
        if (filteredParameters) {
            setFilteredParameters(
                filteredParameters.filter(
                    p => !selected.includes(p)));
        }
        setSelected([]);
    }, [selected, setSelected, setFilteredParameters, filteredParameters, effect]);

    const group = useCallback((shouldGroup) => {
        if (shouldGroup === undefined) {
            // of any of the parameters has a group then it should ne an ungroup
            shouldGroup = !selected.some(
                p => p.hasGroup);
        }

        if (shouldGroup) {
            effect.groupParameters(selected);
        } else {
            let group = selected.find(x => x.hasGroup).hasGroup;
            if (selected.every(s => s.group === s)) {
                // every selected param is of the same group so ungroup the
                // entire thing
                effect.ungroup(group);
            } else {
                // otherwise remove the selected from their groups
                effect.ungroupParameters(selected);
            }
        }
        setSelected([... selected]);
    }, [selected, effect, setSelected]);

    return {unmap, group};
};
